/**
 * Photo strip utilities
 * Creates a classic photo booth strip from individual photos
 */

/**
 * Generate a photo strip by stitching three photos vertically
 * Returns a data URL of the generated strip image
 */
export async function generatePhotoStrip(
  photoDataUrls: string[],
  organizationName?: string
): Promise<string> {
  if (photoDataUrls.length !== 3) {
    throw new Error('Photo strip requires exactly 3 photos');
  }

  // Load all images including the logo
  const [photo1, photo2, photo3, logo] = await Promise.all([
    loadImage(photoDataUrls[0]),
    loadImage(photoDataUrls[1]),
    loadImage(photoDataUrls[2]),
    loadImage('/assets/images/logo-long-blue.png'),
  ]);
  const images = [photo1, photo2, photo3];

  // Photo strip dimensions
  const STRIP_WIDTH = 600; // Classic photo strip width
  const SPACING = 15; // Space between photos
  const BORDER = 20; // Gradient border around the entire strip
  const INNER_PADDING = 15; // Gap between border and photos
  const BRANDING_HEIGHT = 200; // Height for logo at bottom (includes whitespace)

  // Pre-calculate photo heights based on aspect ratios
  const photoWidth = STRIP_WIDTH - BORDER * 2 - INNER_PADDING * 2;
  const photoHeights = images.map((img) => {
    const isVertical = img.height > img.width;
    if (isVertical) {
      // Force 4:3 aspect ratio for vertical photos
      return photoWidth * (4 / 3);
    }
    const aspectRatio = img.width / img.height;
    return photoWidth / aspectRatio;
  });

  // Calculate total height based on actual photo heights
  const totalPhotoHeight = photoHeights.reduce((sum, h) => sum + h, 0);
  const totalHeight =
    BORDER * 2 +
    INNER_PADDING * 2 +
    totalPhotoHeight +
    SPACING * 2 +
    BRANDING_HEIGHT;

  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.width = STRIP_WIDTH;
  canvas.height = totalHeight;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  // Fill with gradient border (Sunday Best brand colors)
  const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0, '#264653'); // Deep blue-green
  gradient.addColorStop(0.25, '#2A9D8F'); // Teal
  gradient.addColorStop(0.5, '#E9C46A'); // Warm yellow
  gradient.addColorStop(0.75, '#F4A261'); // Soft orange
  gradient.addColorStop(1, '#E76F51'); // Coral red
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Fill inner area with off-white background (with rounded corners)
  const innerX = BORDER;
  const innerY = BORDER;
  const innerWidth = STRIP_WIDTH - BORDER * 2;
  const innerHeight = totalHeight - BORDER * 2;
  const INNER_RADIUS = 4;

  ctx.fillStyle = '#FAF7F2'; // Warm off-white
  ctx.beginPath();
  ctx.moveTo(innerX + INNER_RADIUS, innerY);
  ctx.lineTo(innerX + innerWidth - INNER_RADIUS, innerY);
  ctx.arcTo(
    innerX + innerWidth,
    innerY,
    innerX + innerWidth,
    innerY + INNER_RADIUS,
    INNER_RADIUS
  );
  ctx.lineTo(innerX + innerWidth, innerY + innerHeight - INNER_RADIUS);
  ctx.arcTo(
    innerX + innerWidth,
    innerY + innerHeight,
    innerX + innerWidth - INNER_RADIUS,
    innerY + innerHeight,
    INNER_RADIUS
  );
  ctx.lineTo(innerX + INNER_RADIUS, innerY + innerHeight);
  ctx.arcTo(
    innerX,
    innerY + innerHeight,
    innerX,
    innerY + innerHeight - INNER_RADIUS,
    INNER_RADIUS
  );
  ctx.lineTo(innerX, innerY + INNER_RADIUS);
  ctx.arcTo(innerX, innerY, innerX + INNER_RADIUS, innerY, INNER_RADIUS);
  ctx.closePath();
  ctx.fill();

  // Draw each photo
  let currentY = BORDER + INNER_PADDING;
  const PHOTO_RADIUS = 4; // Small rounded corners

  for (let i = 0; i < images.length; i++) {
    const img = images[i];

    // Calculate dimensions to maintain aspect ratio
    const aspectRatio = img.width / img.height;
    const isVertical = img.height > img.width;
    let drawWidth = photoWidth;
    let drawHeight = photoWidth / aspectRatio;

    // Setup source cropping region
    const sourceX = 0;
    let sourceY = 0;
    let sourceWidth = img.width;
    let sourceHeight = img.height;

    if (isVertical) {
      // Force 4:3 aspect ratio for vertical photos
      drawHeight = photoWidth * (4 / 3);
      drawWidth = photoWidth;

      // Use "cover" behavior - fill the area and crop evenly from top and bottom
      // Calculate how much of the source image we need to fill 4:3
      const targetAspectRatio = 3 / 4;
      sourceWidth = img.width;
      sourceHeight = img.width / targetAspectRatio;
      // Center the crop vertically
      sourceY = (img.height - sourceHeight) / 2;
    }

    // Center the photo horizontally if it's narrower than the strip
    const xOffset = (photoWidth - drawWidth) / 2 + BORDER + INNER_PADDING;

    // Draw the photo with rounded corners
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(xOffset + PHOTO_RADIUS, currentY);
    ctx.lineTo(xOffset + drawWidth - PHOTO_RADIUS, currentY);
    ctx.arcTo(
      xOffset + drawWidth,
      currentY,
      xOffset + drawWidth,
      currentY + PHOTO_RADIUS,
      PHOTO_RADIUS
    );
    ctx.lineTo(xOffset + drawWidth, currentY + drawHeight - PHOTO_RADIUS);
    ctx.arcTo(
      xOffset + drawWidth,
      currentY + drawHeight,
      xOffset + drawWidth - PHOTO_RADIUS,
      currentY + drawHeight,
      PHOTO_RADIUS
    );
    ctx.lineTo(xOffset + PHOTO_RADIUS, currentY + drawHeight);
    ctx.arcTo(
      xOffset,
      currentY + drawHeight,
      xOffset,
      currentY + drawHeight - PHOTO_RADIUS,
      PHOTO_RADIUS
    );
    ctx.lineTo(xOffset, currentY + PHOTO_RADIUS);
    ctx.arcTo(
      xOffset,
      currentY,
      xOffset + PHOTO_RADIUS,
      currentY,
      PHOTO_RADIUS
    );
    ctx.closePath();
    ctx.clip();

    // Draw with source cropping if needed
    ctx.drawImage(
      img,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      xOffset,
      currentY,
      drawWidth,
      drawHeight
    );
    ctx.restore();

    // Advance by the actual drawn height plus spacing
    currentY += drawHeight + SPACING;
  }

  // Add organization name in the whitespace
  if (organizationName) {
    currentY += INNER_PADDING + 40; // Add spacing after photos

    ctx.fillStyle = '#264653'; // Deep blue-green from brand colors
    ctx.font = '48px Caveat';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(organizationName, STRIP_WIDTH / 2, currentY);
  }

  // Add logo at the bottom
  currentY += INNER_PADDING;

  // Calculate logo dimensions (smaller logo)
  const maxLogoWidth = STRIP_WIDTH * 0.35;
  const logoAspectRatio = logo.width / logo.height;
  let logoWidth = maxLogoWidth;
  let logoHeight = maxLogoWidth / logoAspectRatio;

  // If logo is too tall, scale by height
  const maxLogoHeight = 30;
  if (logoHeight > maxLogoHeight) {
    logoHeight = maxLogoHeight;
    logoWidth = maxLogoHeight * logoAspectRatio;
  }

  // Center the logo horizontally and position at bottom of branding area
  const logoX = (STRIP_WIDTH - logoWidth) / 2;
  const logoY = totalHeight - BORDER - INNER_PADDING - logoHeight;

  ctx.drawImage(logo, logoX, logoY, logoWidth, logoHeight);

  // Convert to data URL
  return canvas.toDataURL('image/jpeg', 0.95);
}

/**
 * Load an image from a data URL
 */
function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = dataUrl;
  });
}
